<template>
  <div class="c2c-date" ref="rootEl">
    <div class="c2c-date-row">
      <input
        :id="inputId"
        type="text"
        class="c2c-date-input"
        inputmode="numeric"
        autocomplete="off"
        spellcheck="false"
        maxlength="10"
        :placeholder="t('form.date_placeholder')"
        :value="text"
        :aria-invalid="!!error"
        :aria-describedby="error ? errorId : undefined"
        :class="{ 'c2c-date-input--bad': !!error }"
        @beforeinput="onBeforeInput"
        @input="onInput"
        @keydown.enter.prevent="commit"
        @blur="commit"
      />
      <button
        type="button"
        class="c2c-date-btn"
        :class="{ 'c2c-date-btn--open': open }"
        :aria-label="t('form.date_pick_aria')"
        :aria-expanded="open"
        @click="toggle"
      >
        <CalendarDays :size="18" />
      </button>
    </div>

    <small v-if="error" :id="errorId" class="c2c-field-error" role="alert">{{ error }}</small>

    <div v-if="open" class="c2c-cal" role="dialog" :aria-label="t('form.date_pick_aria')">
      <div class="c2c-cal-head">
        <button type="button" class="c2c-cal-nav" :aria-label="prevLabel" @click="shiftView(-1)">
          <ChevronLeft :size="18" />
        </button>
        <!--
          Footage from two years ago used to mean twenty-four clicks on the
          back arrow. The title is a button: days to months to years, the
          drill-down every date picker worth using has.
        -->
        <button
          type="button"
          class="c2c-cal-title"
          :aria-label="t('form.cal_zoom_out')"
          :disabled="view === 'years'"
          @click="zoomOut"
        >
          <span>{{ headingLabel }}</span>
          <ChevronDown v-if="view !== 'years'" :size="14" aria-hidden="true" />
        </button>
        <button type="button" class="c2c-cal-nav" :aria-label="nextLabel" @click="shiftView(1)">
          <ChevronRight :size="18" />
        </button>
      </div>

      <div v-if="view === 'days'" class="c2c-cal-grid">
        <span v-for="(d, i) in weekdayLabels" :key="'dow' + i" class="c2c-cal-dow">{{ d }}</span>
        <button
          v-for="cell in cells"
          :key="cell.iso"
          type="button"
          class="c2c-cal-day"
          :class="{
            'c2c-cal-day--out': !cell.inMonth,
            'c2c-cal-day--today': cell.iso === todayIso,
            'c2c-cal-day--sel': cell.iso === modelValue
          }"
          :disabled="!!max && cell.iso > max"
          :aria-current="cell.iso === todayIso ? 'date' : undefined"
          @click="pick(cell.iso)"
        >{{ cell.day }}</button>
      </div>

      <div v-else-if="view === 'months'" class="c2c-cal-chunks">
        <button
          v-for="(name, i) in monthNames"
          :key="'m' + i"
          type="button"
          class="c2c-cal-chunk"
          :class="{ 'c2c-cal-chunk--sel': i === viewMonth }"
          :disabled="monthDisabled(i)"
          @click="pickMonth(i)"
        >{{ name }}</button>
      </div>

      <div v-else class="c2c-cal-chunks">
        <button
          v-for="y in yearChoices"
          :key="'y' + y"
          type="button"
          class="c2c-cal-chunk"
          :class="{ 'c2c-cal-chunk--sel': y === viewYear }"
          :disabled="yearDisabled(y)"
          @click="pickYear(y)"
        >{{ y }}</button>
      </div>

      <div class="c2c-cal-foot">
        <button type="button" class="c2c-cal-today" @click="pick(todayIso)">{{ t('form.cal_today') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Date entry that is typed first and picked second.
 *
 * GOV.UK's date pattern uses plain text fields rather than a calendar widget
 * because people usually know the date they are entering, and MDN documents
 * that the native control cannot be styled reliably (its generated content
 * is closed to CSS in Chrome) and that its displayed format follows the
 * browser locale while its value is always ISO. So: a text field we control,
 * with our own popover for the times when browsing is genuinely easier.
 *
 * The field is masked as you type, the way a card-number field is: type
 * eight digits and the separators appear on their own. Nobody should have to
 * reach for the hyphen key. Each part is checked the moment it is complete,
 * so 69 as a day is rejected at the second keystroke rather than at blur.
 *
 * The value in and out is always ISO yyyy-mm-dd, which is what the wikitext
 * date field needs. What the contributor sees and types is dd-mm-yyyy.
 */
import { computed, onBeforeUnmount, ref, useId, watch } from "vue"
import { useI18n } from "vue-i18n"
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-vue-next"

const { t, locale } = useI18n()

const props = defineProps<{
  modelValue: string
  /** ISO upper bound, inclusive. Days past it are not selectable. */
  max?: string
  inputId?: string
}>()
const emit = defineEmits<{
  "update:modelValue": [string]
  /**
   * Whether what is in the field right now could be uploaded.
   *
   * Refusing a bad date here used to be cosmetic: the model kept its last
   * good value, so a form showing "Month must be between 01 and 12" still
   * had a valid date behind it and the queue started happily. The parent
   * watches this to hold the submit button.
   */
  "update:valid": [boolean]
}>()

const open = ref(false)
const error = ref("")
const rootEl = ref<HTMLElement | null>(null)
const errorId = "c2c-date-err-" + useId()

const todayIso = new Date().toISOString().slice(0, 10)

// immediate, so a fresh instance announces itself rather than leaving the
// parent holding the previous instance's verdict. Without it, rejecting a
// date and then starting a new upload left the submit button disabled with
// nothing on screen to fix.
watch(error, (e) => emit("update:valid", !e), { immediate: true })

/* ------------------------------------------------------------------ *
 *  Mask. Eight digits in, dd-mm-yyyy out.                             *
 * ------------------------------------------------------------------ */

/** Digits only, capped at eight. One pass, no regex, no backtracking. */
function digitsOf(s: string): string {
  let out = ""
  for (let i = 0; i < s.length && out.length < 8; i++) {
    const c = s.charCodeAt(i)
    if (c >= 48 && c <= 57) out += s[i]
  }
  return out
}

/** 1202 becomes 12-02; 12022025 becomes 12-02-2025. */
function mask(d: string): string {
  if (d.length <= 2) return d
  if (d.length <= 4) return d.slice(0, 2) + "-" + d.slice(2)
  return d.slice(0, 2) + "-" + d.slice(2, 4) + "-" + d.slice(4)
}

function isoToDigits(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "")
  return m ? m[3] + m[2] + m[1] : ""
}

/** Real calendar date? Rejects 31-02 and friends, which Date rolls over. */
function isRealDate(y: number, mo: number, d: number): boolean {
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1) return false
  const probe = new Date(Date.UTC(y, mo - 1, d))
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d
}

/** ddmmyyyy to yyyy-mm-dd. */
function toIso(d: string): string {
  return d.slice(4) + "-" + d.slice(2, 4) + "-" + d.slice(0, 2)
}

const digits = ref(isoToDigits(props.modelValue))
const text = computed(() => mask(digits.value))

watch(() => props.modelValue, (iso) => {
  const incoming = isoToDigits(iso)
  if (incoming !== digits.value) {
    digits.value = incoming
    error.value = ""
  }
})

/**
 * Checks each part the moment it is complete. Returns a message, or an empty
 * string while what has been typed so far can still become a valid date.
 */
function liveError(d: string): string {
  if (d.length >= 2) {
    const day = +d.slice(0, 2)
    if (day < 1 || day > 31) return t("form.date_err_day")
  }
  if (d.length >= 4) {
    const mo = +d.slice(2, 4)
    if (mo < 1 || mo > 12) return t("form.date_err_month")
  }
  if (d.length === 8) {
    const y = +d.slice(4)
    if (y < 1000) return t("form.date_err_year")
    if (!isRealDate(y, +d.slice(2, 4), +d.slice(0, 2))) return t("form.date_err_nonexistent")
    if (props.max && toIso(d) > props.max) return t("form.date_future")
  }
  return ""
}

/** Digits and date separators only. Letters never reach the field. */
function onBeforeInput(e: Event) {
  const data = (e as InputEvent).data
  if (data && /[^0-9/.\-]/.test(data)) e.preventDefault()
}

function onInput(e: Event) {
  const el = e.target as HTMLInputElement
  const raw = el.value
  const caret = el.selectionStart ?? raw.length
  // How many digits sat left of the caret before the reformat. Re-finding
  // that count in the masked string is what stops the caret jumping to the
  // end when someone edits the middle.
  let before = 0
  for (let i = 0; i < caret; i++) {
    const c = raw.charCodeAt(i)
    if (c >= 48 && c <= 57) before++
  }

  let next = digitsOf(raw)
  // Backspacing over a separator removes no digit, so the mask would put it
  // straight back and the caret would sit there for ever. Eat the digit in
  // front of it instead, which is what was meant.
  if ((e as InputEvent).inputType === "deleteContentBackward" &&
      next.length === digits.value.length && next.length > 0) {
    next = next.slice(0, -1)
    if (before > 0) before--
  }

  digits.value = next
  error.value = liveError(next)

  // A complete, valid date publishes immediately. No blur required.
  if (!error.value && next.length === 8) {
    const iso = toIso(next)
    if (iso !== props.modelValue) emit("update:modelValue", iso)
  }

  // The DOM value is written here rather than left to the re-render, because
  // the caret has to be restored in the same tick as the text it indexes.
  const masked = mask(next)
  let pos = masked.length
  let seen = 0
  for (let i = 0; i < masked.length; i++) {
    if (seen === before) { pos = i; break }
    const c = masked.charCodeAt(i)
    if (c >= 48 && c <= 57) seen++
  }
  if (before > 0 && pos < masked.length && masked[pos] === "-") pos++
  el.value = masked
  el.setSelectionRange(pos, pos)
}

function commit() {
  const d = digits.value
  if (!d) { error.value = t("form.date_required"); return }
  if (d.length < 8) { error.value = t("form.date_incomplete"); return }
  error.value = liveError(d)
  if (error.value) return
  const iso = toIso(d)
  if (iso !== props.modelValue) emit("update:modelValue", iso)
}

/* ------------------------------------------------------------------ *
 *  Calendar popover                                                   *
 * ------------------------------------------------------------------ */

const viewYear = ref(0)
const viewMonth = ref(0)
/** Which zoom level the popover is showing. */
const view = ref<"days" | "months" | "years">("days")

/** Years are offered a dozen at a time, aligned so the grid never shifts. */
const YEARS_PER_PAGE = 12

function syncView() {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(props.modelValue) ? props.modelValue : todayIso
  viewYear.value = +base.slice(0, 4)
  viewMonth.value = +base.slice(5, 7) - 1
  view.value = "days"
}

function toggle() {
  open.value = !open.value
  if (!open.value) return
  syncView()
  document.addEventListener("pointerdown", onOutside, true)
  document.addEventListener("keydown", onEsc, true)
}

function close() {
  open.value = false
  document.removeEventListener("pointerdown", onOutside, true)
  document.removeEventListener("keydown", onEsc, true)
}

function onOutside(e: Event) {
  if (!rootEl.value?.contains(e.target as Node)) close()
}
function onEsc(e: KeyboardEvent) {
  if (e.key === "Escape") close()
}
onBeforeUnmount(close)

function shiftMonth(delta: number) {
  const m = viewMonth.value + delta
  viewYear.value += Math.floor(m / 12)
  viewMonth.value = ((m % 12) + 12) % 12
}

/** The arrows step by whatever the current level is made of. */
function shiftView(delta: number) {
  if (view.value === "days") shiftMonth(delta)
  else if (view.value === "months") viewYear.value += delta
  else viewYear.value += delta * YEARS_PER_PAGE
}

function zoomOut() {
  view.value = view.value === "days" ? "months" : "years"
}

function pickMonth(index: number) {
  viewMonth.value = index
  view.value = "days"
}

function pickYear(year: number) {
  viewYear.value = year
  view.value = "months"
}

const maxYear = computed(() => props.max ? +props.max.slice(0, 4) : Infinity)
const maxMonth = computed(() => props.max ? +props.max.slice(5, 7) - 1 : 11)

function yearDisabled(year: number): boolean {
  return year > maxYear.value
}

function monthDisabled(index: number): boolean {
  return viewYear.value > maxYear.value ||
    (viewYear.value === maxYear.value && index > maxMonth.value)
}

const yearPageStart = computed(() =>
  Math.floor(viewYear.value / YEARS_PER_PAGE) * YEARS_PER_PAGE)

const yearChoices = computed(() => {
  const out: number[] = []
  for (let i = 0; i < YEARS_PER_PAGE; i++) out.push(yearPageStart.value + i)
  return out
})

/** Month names in the interface language, for the month grid. */
const monthNames = computed(() => {
  const fmt = new Intl.DateTimeFormat(String(locale.value || "en"), { month: "short" })
  const out: string[] = []
  for (let i = 0; i < 12; i++) out.push(fmt.format(new Date(Date.UTC(2024, i, 1))))
  return out
})

const headingLabel = computed(() => {
  if (view.value === "days") return monthLabel.value
  if (view.value === "months") return String(viewYear.value)
  return yearPageStart.value + " to " + (yearPageStart.value + YEARS_PER_PAGE - 1)
})

const prevLabel = computed(() =>
  view.value === "days" ? t("form.cal_prev")
    : view.value === "months" ? t("form.cal_prev_year") : t("form.cal_prev_years"))
const nextLabel = computed(() =>
  view.value === "days" ? t("form.cal_next")
    : view.value === "months" ? t("form.cal_next_year") : t("form.cal_next_years"))

function pick(iso: string) {
  if (props.max && iso > props.max) return
  digits.value = isoToDigits(iso)
  error.value = ""
  emit("update:modelValue", iso)
  close()
}

const monthLabel = computed(() =>
  new Intl.DateTimeFormat(String(locale.value || "en"), { month: "long", year: "numeric" })
    .format(new Date(Date.UTC(viewYear.value, viewMonth.value, 1))))

/** Monday-first weekday initials in the interface language. */
const weekdayLabels = computed(() => {
  const fmt = new Intl.DateTimeFormat(String(locale.value || "en"), { weekday: "short" })
  const out: string[] = []
  for (let i = 0; i < 7; i++) out.push(fmt.format(new Date(Date.UTC(2024, 0, 1 + i))))
  return out
})

const cells = computed(() => {
  const first = new Date(Date.UTC(viewYear.value, viewMonth.value, 1))
  // Monday-first: getUTCDay is 0 for Sunday.
  const lead = (first.getUTCDay() + 6) % 7
  const out: Array<{ iso: string; day: number; inMonth: boolean }> = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(Date.UTC(viewYear.value, viewMonth.value, 1 - lead + i))
    out.push({
      iso: d.toISOString().slice(0, 10),
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === viewMonth.value
    })
  }
  return out
})
</script>

<style scoped>
.c2c-date { position: relative; display: flex; flex-direction: column; gap: 3px; }
.c2c-date-row { display: flex; gap: 8px; align-items: stretch; }

.c2c-date-input {
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  padding: 10px 12px;
  font: inherit;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  min-height: 44px;
  border: 1px solid #a2a9b1;
  border-radius: 6px;
  background: #fff;
  color: #202122;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
/* One ring, never two: a transparent outline keeps the focus state visible
   under forced colours while the glow does the work everywhere else. */
.c2c-date-input:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-date-input--bad { border-color: #b32424; }
.c2c-date-input--bad:focus {
  border-color: #b32424;
  box-shadow: 0 0 0 3px rgba(179, 36, 36, 0.22);
}

.c2c-date-btn {
  flex: 0 0 auto;
  width: 44px; min-height: 44px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; color: #202122; cursor: pointer;
  border: 1px solid #a2a9b1; border-radius: 6px;
  transition: background-color 120ms ease-out, border-color 120ms ease-out;
}
.c2c-date-btn:hover { background: #eaecf0; }
.c2c-date-btn--open { border-color: #3366cc; color: #3366cc; background: #eaf3ff; }
.c2c-date-btn:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}

/* Popover */
.c2c-cal {
  position: absolute; z-index: 30; top: calc(100% + 6px); left: 0;
  width: 288px; padding: 10px;
  background: #fff;
  border: 1px solid #c8ccd1;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08);
}
.c2c-cal-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; margin-bottom: 8px;
}
.c2c-cal-title {
  display: inline-flex; align-items: center; gap: 4px;
  flex: 1 1 auto; justify-content: center;
  padding: 6px 10px; border-radius: 8px;
  background: transparent; border: 1px solid transparent;
  font: inherit; font-size: 14px; font-weight: 700; color: #202122;
  cursor: pointer;
  transition: background-color 120ms ease-out;
}
.c2c-cal-title:hover:not(:disabled) { background: #eaecf0; }
.c2c-cal-title:disabled { cursor: default; }
.c2c-cal-title:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}

/* Month and year grids. Three across keeps a four-row block the same
   height as the day grid, so the popover does not jump as you drill in. */
.c2c-cal-chunks {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
}
.c2c-cal-chunk {
  height: 44px; border: 0; border-radius: 8px;
  background: transparent; color: #202122;
  font: inherit; font-size: 13px; font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: background-color 90ms ease-out;
}
.c2c-cal-chunk:hover:not(:disabled) { background: #eaf3ff; }
.c2c-cal-chunk:focus-visible { outline: 2px solid #3366cc; outline-offset: -2px; }
.c2c-cal-chunk--sel { background: #3366cc; color: #fff; font-weight: 700; }
.c2c-cal-chunk--sel:hover:not(:disabled) { background: #2a5cb8; }
.c2c-cal-chunk:disabled { color: #dadde1; cursor: not-allowed; }
.c2c-cal-nav {
  width: 32px; height: 32px; border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; color: #54595d; cursor: pointer;
}
.c2c-cal-nav:hover { background: #eaecf0; color: #202122; }
.c2c-cal-nav:focus-visible { outline: 2px solid #3366cc; outline-offset: 1px; }

.c2c-cal-grid {
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;
}
.c2c-cal-dow {
  font-size: 11px; font-weight: 700; color: #72777d;
  text-align: center; padding-bottom: 4px; text-transform: uppercase;
}
.c2c-cal-day {
  height: 34px; border: 0; border-radius: 8px;
  background: transparent; color: #202122;
  font: inherit; font-size: 13px; font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: background-color 90ms ease-out;
}
.c2c-cal-day:hover:not(:disabled) { background: #eaf3ff; }
.c2c-cal-day:focus-visible { outline: 2px solid #3366cc; outline-offset: -2px; }
.c2c-cal-day--out { color: #c8ccd1; }
.c2c-cal-day--today { box-shadow: inset 0 0 0 1px #a2a9b1; font-weight: 700; }
.c2c-cal-day--sel {
  background: #3366cc; color: #fff; font-weight: 700;
}
.c2c-cal-day--sel:hover:not(:disabled) { background: #2a5cb8; }
.c2c-cal-day:disabled { color: #dadde1; cursor: not-allowed; }

.c2c-cal-foot { display: flex; justify-content: flex-end; margin-top: 8px; }
.c2c-cal-today {
  background: none; border: 0; padding: 6px 8px; border-radius: 6px;
  font: inherit; font-size: 12px; color: #3366cc; cursor: pointer;
}
.c2c-cal-today:hover { background: #eaf3ff; }

.c2c-field-error { color: #b32424; font-size: 12px; }
</style>
